import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/client";
import useAuthStore from "../../store/authStore";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";

export default function EmployerLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");

  const setEmployerAuth = useAuthStore((s) => s.setEmployerAuth);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Joborigo — Employer Portal";
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/employer/auth/login", form, {
        withCredentials: true,
      });
      setEmployerAuth(data.access_token, data.employer);
      // Redirect to next if present, otherwise dashboard
      navigate(next ? decodeURIComponent(next) : "/employers/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex flex-col">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
        <Logo className="h-12" />
        <ThemeToggle />
      </nav>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-8 w-full max-w-sm">
          <span className="text-xs font-medium text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest">
            Employer portal
          </span>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1 mb-1">
            Welcome back
          </h1>
          <p className="text-gray-400 text-sm mb-6">
            Sign in to your employer account
          </p>

          {/* Show context banner if coming from a connect link */}
          {next?.includes("connect=") && (
            <div className="mb-4 bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-lg px-4 py-3">
              <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark leading-relaxed">
                🔗 Sign in to connect a candidate and send them real-time status
                updates.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <input
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="you@company.com"
              className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
            />
            <input
              name="password"
              type="password"
              required
              value={form.password}
              onChange={handleChange}
              placeholder="Password"
              className="border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-violet text-white py-2.5 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-6">
            No account?{" "}
            <Link
              to="/employers/register"
              className="text-brand-violet dark:text-brand-violet-on-dark font-medium hover:underline"
            >
              Register your company
            </Link>
          </p>
          <p className="text-center mt-3">
            <Link
              to="/"
              className="text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark text-xs"
            >
              ← Back to Joborigo
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
